import { useState, useEffect, useCallback } from 'react';
import { trpc } from '../api/trpc';

// ─── Types ──────────────────────────────────────────────────────────────────

type WizardState =
  | 'choose_permissions'
  | 'awaiting_installer_consent'
  | 'provisioning'
  | 'awaiting_admin_consent'
  | 'verifying'
  | 'success'
  | 'failed'
  | 'cancelled';

type Preset = 'readonly' | 'read_response' | 'custom';

interface Permission {
  id: string;
  resourceAppId: string;
  resourceName: string;
  value: string;
  displayName: string;
  description: string;
  shortCapability: string;
  tier: string;
  category: string;
  isRequired: boolean;
  isWrite: boolean;
  isRecommended: boolean;
}

interface ProvisioningRecord {
  id: string;
  state: string;
  customerTenantId?: string | null;
  customerTenantName?: string | null;
  connectorAppId?: string | null;
  connectorObjectId?: string | null;
  servicePrincipalId?: string | null;
  connectorId?: string | null;
  failureReason?: string | null;
}

interface Props {
  onComplete: () => void;
  onFallbackToManual: () => void;
  onStateChange?: (state: string) => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const STEPS = ['Permissions', 'Install', 'Consent', 'Test'] as const;

function stepIndexForState(state: WizardState): number {
  switch (state) {
    case 'choose_permissions': return 0;
    case 'awaiting_installer_consent':
    case 'provisioning': return 1;
    case 'awaiting_admin_consent': return 2;
    case 'verifying':
    case 'success': return 3;
    default: return 0;
  }
}

/** True when the wizard is in a state where dismissing would lose progress */
export function isProvisioningInProgress(state: WizardState): boolean {
  return ['awaiting_installer_consent', 'provisioning', 'awaiting_admin_consent', 'verifying'].includes(state);
}

// ─── Preset logic ───────────────────────────────────────────────────────────

function getPresetPermissions(permissions: Permission[], preset: Preset): Set<string> {
  const ids = new Set<string>();
  for (const p of permissions) {
    if (p.isRequired) ids.add(p.id);
  }
  if (preset === 'readonly' || preset === 'read_response') {
    for (const p of permissions) { if (p.tier === 'readonly') ids.add(p.id); }
  }
  if (preset === 'read_response') {
    for (const p of permissions) { ids.add(p.id); }
  }
  return ids;
}

// ─── Small UI helpers ───────────────────────────────────────────────────────

function Spinner({ size = 28 }: { size?: number }) {
  return (
    <div style={{
      width: size, height: size, border: '3px solid var(--color-border)',
      borderTopColor: 'var(--color-accent)', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite', margin: '0 auto',
    }} />
  );
}

function CopyButton({ text, label = 'Copy link', disabled }: { text: string; label?: string; disabled?: boolean }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (!text) return;
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button type="button" className="btn-secondary" onClick={handleCopy} disabled={disabled || !text} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
      {copied ? 'Copied!' : label}
    </button>
  );
}

/** Build a shareable message with the consent URL */
function buildConsentMessage(url: string, phase: 'installer' | 'admin'): string {
  if (phase === 'installer') {
    return [
      `Hi,`,
      ``,
      `Please approve the Cade Installer app to connect our Microsoft tenant to Cade.`,
      `This grants Cade limited permissions to create and manage a connector app registration in our tenant.`,
      ``,
      `Click the link below and sign in with a Cloud Application Administrator or Global Administrator account:`,
      ``,
      url,
      ``,
      `The Installer only requests Application.ReadWrite.OwnedBy (to create apps it owns) and User.Read (for audit).`,
    ].join('\n');
  }
  return [
    `Hi,`,
    ``,
    `Please grant admin consent for the Cade Security Connector app in our Microsoft tenant.`,
    `This will allow Cade to read security data from Microsoft Graph (users, alerts, incidents, etc.) using the permissions listed on the consent screen.`,
    ``,
    `Click the link below and sign in with an admin account:`,
    ``,
    url,
    ``,
    `You can review the requested permissions on the Microsoft consent page before approving.`,
  ].join('\n');
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--color-text-primary)', fontFamily: mono ? 'var(--font-mono)' : undefined, fontSize: mono ? 11 : undefined }}>{value}</span>
    </div>
  );
}

function ProvisioningStep({ label, done, active }: { label: string; done: boolean; active: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
      {done ? (
        <span style={{ color: 'var(--sev-success)', fontSize: 16, width: 20, textAlign: 'center' }}>{'\u2713'}</span>
      ) : active ? (
        <Spinner size={16} />
      ) : (
        <span style={{ color: 'var(--color-border)', fontSize: 16, width: 20, textAlign: 'center' }}>{'\u25CB'}</span>
      )}
      <span style={{ fontSize: 13, color: done ? 'var(--color-text-secondary)' : active ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}>
        {label}
      </span>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function MicrosoftAutomatedInstall({ onComplete, onFallbackToManual, onStateChange }: Props) {
  const [wizardState, setWizardState] = useState<WizardState>('choose_permissions');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [preset, setPreset] = useState<Preset>('readonly');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [provisioningId, setProvisioningId] = useState<string | null>(null);
  const [installerConsentUrl, setInstallerConsentUrl] = useState<string | null>(null);
  const [adminConsentUrl, setAdminConsentUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [provisioningRecord, setProvisioningRecord] = useState<ProvisioningRecord | null>(null);
  const [provisioningStep, setProvisioningStep] = useState(0); // 0-4 for animated checklist

  // tRPC hooks
  const catalogQuery = trpc.connectors.provisioning.getPermissionCatalog.useQuery();
  const currentQuery = trpc.connectors.provisioning.microsoft.getCurrentProvisioning.useQuery();
  const startMut = trpc.connectors.provisioning.microsoft.startProvisioning.useMutation();
  const cancelMut = trpc.connectors.provisioning.microsoft.cancelProvisioning.useMutation();
  const resumeConsentMut = trpc.connectors.provisioning.microsoft.resumeAdminConsent.useMutation();
  const retryMut = trpc.connectors.provisioning.microsoft.retryFailed.useMutation();

  const permissions = (catalogQuery.data?.permissions ?? []) as Permission[];

  // Notify parent of state changes
  useEffect(() => { onStateChange?.(wizardState); }, [wizardState, onStateChange]);

  // Initialize selected permissions from preset
  useEffect(() => {
    if (permissions.length > 0 && selectedIds.size === 0) {
      setSelectedIds(getPresetPermissions(permissions, 'readonly'));
    }
  }, [permissions.length]);  

  // Resume existing provisioning if found
  useEffect(() => {
    if (currentQuery.data) {
      const p = currentQuery.data as ProvisioningRecord;
      setProvisioningId(p.id);
      setProvisioningRecord(p);
      if (p.state !== 'pending_installer_consent') {
        setWizardState(p.state as WizardState);
      }
      if (p.failureReason) setError(p.failureReason);
    }
  }, [currentQuery.data]);

  // Eagerly fetch admin consent URL when entering awaiting_admin_consent
  useEffect(() => {
    if (wizardState === 'awaiting_admin_consent' && provisioningId && !adminConsentUrl) {
      resumeConsentMut.mutateAsync({ provisioningId })
        .then(result => setAdminConsentUrl(result.adminConsentUrl))
        .catch(() => {}); // non-fatal, user can click the button
    }
  }, [wizardState, provisioningId]);  

  // Poll for state changes when in transitional states
  useEffect(() => {
    if (!provisioningId) return;
    if (!['awaiting_installer_consent', 'provisioning', 'verifying', 'awaiting_admin_consent'].includes(wizardState)) return;

    const interval = setInterval(async () => {
      const result = await currentQuery.refetch();
      const p = result.data as ProvisioningRecord | null;
      if (p) {
        setProvisioningRecord(p);
        if (p.state !== wizardState) {
          setWizardState(p.state as WizardState);
          if (p.failureReason) setError(p.failureReason);
        }
      } else if (wizardState === 'verifying' || wizardState === 'provisioning') {
        // Record no longer in-progress — provisioning completed (moved to active/failed/cancelled)
        // Check connectors list to confirm it was created
        setWizardState('success');
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [provisioningId, wizardState]);  

  // Animate provisioning step checklist
  useEffect(() => {
    if (wizardState !== 'provisioning') { setProvisioningStep(0); return; }
    const timers = [
      setTimeout(() => setProvisioningStep(1), 2000),
      setTimeout(() => setProvisioningStep(2), 6000),
      setTimeout(() => setProvisioningStep(3), 9000),
      setTimeout(() => setProvisioningStep(4), 12000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [wizardState]);

  // Check URL params for callback results
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlProvId = params.get('provisioningId');
    const urlStep = params.get('step');
    const urlError = params.get('error');

    if (urlProvId) {
      setProvisioningId(urlProvId);
      if (urlStep) setWizardState(urlStep as WizardState);
      if (urlError) { setError(urlError); setWizardState('failed'); }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handlePresetChange = useCallback((newPreset: Preset) => {
    setPreset(newPreset);
    if (newPreset !== 'custom') setSelectedIds(getPresetPermissions(permissions, newPreset));
  }, [permissions]);

  const handlePermissionToggle = useCallback((id: string, isRequired: boolean) => {
    if (isRequired) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setPreset('custom');
  }, []);

  const handleInstall = useCallback(async () => {
    setError(null);
    try {
      const result = await startMut.mutateAsync({
        selectedPermissionIds: Array.from(selectedIds),
        preset: preset as 'readonly' | 'read_response' | 'custom',
      });
      setProvisioningId(result.provisioningId);
      setInstallerConsentUrl(result.consentUrl);
      setWizardState('awaiting_installer_consent');
      window.location.href = result.consentUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start provisioning');
    }
  }, [selectedIds, preset, startMut]);

  const handleGrantConsent = useCallback(async () => {
    if (!provisioningId) return;
    try {
      const result = await resumeConsentMut.mutateAsync({ provisioningId });
      setAdminConsentUrl(result.adminConsentUrl);
      window.location.href = result.adminConsentUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get consent URL');
    }
  }, [provisioningId, resumeConsentMut]);

  const handleCancel = useCallback(async () => {
    if (!provisioningId) return;
    try {
      await cancelMut.mutateAsync({ provisioningId });
      setWizardState('cancelled');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    }
  }, [provisioningId, cancelMut]);

  const handleRetry = useCallback(async () => {
    if (!provisioningId) return;
    setError(null);
    try {
      const result = await retryMut.mutateAsync({ provisioningId });
      if ('consentUrl' in result) {
        window.location.href = result.consentUrl as string;
      } else {
        setWizardState('provisioning');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Retry failed');
    }
  }, [provisioningId, retryMut]);

  // ─── Render ─────────────────────────────────────────────────────────────

  const currentStep = stepIndexForState(wizardState);
  const rec = provisioningRecord;

  // Group permissions by category
  const grouped: Record<string, Permission[]> = {};
  for (const p of permissions) {
    if (!grouped[p.category]) grouped[p.category] = [];
    grouped[p.category]!.push(p);
  }

  const readonlyCount = permissions.filter(p => p.tier === 'readonly').length;
  const selectedCapabilities = permissions.filter(p => selectedIds.has(p.id)).map(p => p.shortCapability);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 0' }}>
      {/* CSS for spinner animation */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Progress indicator */}
      <div className="wizard-steps">
        {STEPS.map((label, i) => (
          <div key={label} className={`wizard-step${i <= currentStep ? ' active' : ''}`}>
            <div className={`wizard-step-circle${i < currentStep ? ' completed' : i === currentStep ? ' current' : ''}`}>
              {i < currentStep ? '\u2713' : i + 1}
            </div>
            <span className="wizard-step-label">{label}</span>
          </div>
        ))}
      </div>

      {/* ─── State: choose_permissions ─── */}
      {wizardState === 'choose_permissions' && (
        <div className="wizard-body">
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
            Select the permissions Cade should request from your Microsoft tenant. You must sign in with a Cloud Application Administrator, Application Administrator, or Global Administrator account.
          </p>

          {catalogQuery.isLoading && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Spinner size={24} />
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 8 }}>Loading permissions...</p>
            </div>
          )}

          {catalogQuery.isError && (
            <p className="login-error" style={{ marginBottom: 12 }}>
              Failed to load permissions catalog. Please try refreshing the page.
            </p>
          )}

          {/* Preset selector */}
          {!catalogQuery.isLoading && !catalogQuery.isError && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[
              { value: 'readonly' as Preset, label: 'Read-only', desc: `${readonlyCount} permissions` },
              { value: 'read_response' as Preset, label: 'Read + Response', desc: `${permissions.length} permissions` },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handlePresetChange(opt.value)}
                style={{
                  flex: 1, padding: '10px 12px', borderRadius: 8,
                  border: `2px solid ${preset === opt.value ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  background: preset === opt.value ? 'var(--color-accent-bg)' : 'var(--color-bg-elevated)',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{opt.desc}</div>
              </button>
            ))}
          </div>
          )}

          {/* Advanced picker */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', fontSize: 13, padding: 0, marginBottom: 8 }}
          >
            {showAdvanced ? '\u25BC' : '\u25B6'} Advanced permissions
          </button>

          {showAdvanced && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, maxHeight: 280, overflowY: 'auto', padding: '8px 0' }}>
              {Object.entries(grouped).map(([category, perms]) => (
                <div key={category}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                    {category.replace(/_/g, ' ')}
                  </div>
                  {perms.map(p => (
                    <label key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 0', opacity: p.isRequired ? 0.7 : 1, cursor: p.isRequired ? 'not-allowed' : 'pointer' }}>
                      <input type="checkbox" checked={selectedIds.has(p.id)} disabled={p.isRequired} onChange={() => handlePermissionToggle(p.id, p.isRequired)} style={{ marginTop: 2, width: 'auto' }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>
                          {p.value}
                          {p.isWrite && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--sev-medium)', color: '#fff' }}>Write</span>}
                          {p.isRequired && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--color-text-muted)' }}>Required</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{p.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Permission summary */}
          <div style={{ padding: 12, borderRadius: 8, background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>What Cade will do</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              {selectedCapabilities.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 16 }}>{[...new Set(selectedCapabilities)].map(cap => <li key={cap}>{cap}</li>)}</ul>
              ) : <span>Select permissions above</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 8 }}>{selectedIds.size} permission{selectedIds.size !== 1 ? 's' : ''} selected</div>
          </div>

          {error && <p className="login-error">{error}</p>}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn-primary" disabled={selectedIds.size === 0 || startMut.isPending} onClick={() => { void handleInstall(); }} style={{ flex: 1 }}>
              {startMut.isPending ? (<><Spinner size={14} /> Starting...</>) : 'Install'}
            </button>
            <button type="button" onClick={onFallbackToManual} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
              Manual setup
            </button>
          </div>
        </div>
      )}

      {/* ─── State: awaiting_installer_consent ─── */}
      {wizardState === 'awaiting_installer_consent' && (
        <div className="wizard-body" style={{ padding: '24px 16px' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <Spinner size={32} />
            <p style={{ fontSize: 14, fontWeight: 500, marginTop: 12 }}>Waiting for Microsoft consent...</p>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              Complete the consent in the Microsoft window. This page will update automatically.
            </p>
          </div>

          {installerConsentUrl && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <CopyButton text={installerConsentUrl ? buildConsentMessage(installerConsentUrl, 'installer') : ''} label="Copy consent link" />
              <button type="button" className="btn-secondary" style={{ fontSize: 12 }} onClick={() => { window.open(installerConsentUrl, '_blank'); }}>
                Re-open consent window
              </button>
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button type="button" style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { void handleCancel(); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ─── State: provisioning ─── */}
      {wizardState === 'provisioning' && (
        <div className="wizard-body" style={{ padding: '24px 16px' }}>
          <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 16 }}>Setting up your connector...</p>

          <div style={{ padding: '8px 0', marginBottom: 16 }}>
            <ProvisioningStep label="Creating app registration" done={provisioningStep >= 1} active={provisioningStep === 0} />
            <ProvisioningStep label="Generating credentials" done={provisioningStep >= 2} active={provisioningStep === 1} />
            <ProvisioningStep label="Configuring permissions" done={provisioningStep >= 3} active={provisioningStep === 2} />
            <ProvisioningStep label="Finalizing setup" done={provisioningStep >= 4} active={provisioningStep === 3} />
          </div>

          {rec?.customerTenantName && (
            <div style={{ padding: 10, borderRadius: 8, background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', fontSize: 12 }}>
              <InfoRow label="Tenant" value={rec.customerTenantName} />
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button type="button" style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { void handleCancel(); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ─── State: awaiting_admin_consent ─── */}
      {wizardState === 'awaiting_admin_consent' && (
        <div className="wizard-body" style={{ padding: '24px 16px' }}>
          <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Grant admin consent</p>
          <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
            The connector app has been created in your tenant. Grant admin consent to activate the requested permissions.
          </p>

          {/* App details */}
          <div style={{ padding: 12, borderRadius: 8, background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>App created</div>
            {rec?.customerTenantName && <InfoRow label="Tenant" value={rec.customerTenantName} />}
            {rec?.connectorAppId && <InfoRow label="App ID" value={rec.connectorAppId} mono />}
            <InfoRow label="App name" value="Cade Security Connector" />
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button type="button" className="btn-primary" onClick={() => { void handleGrantConsent(); }} disabled={resumeConsentMut.isPending} style={{ flex: 1 }}>
              {resumeConsentMut.isPending ? (<><Spinner size={14} /> Loading...</>) : 'Grant consent in Microsoft'}
            </button>
            <CopyButton text={adminConsentUrl ? buildConsentMessage(adminConsentUrl, 'admin') : ''} label="Copy link" />
          </div>

          {!adminConsentUrl && (
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'center' }}>
              Loading consent link...
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
            <button type="button" style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { void handleCancel(); }}>
              Cancel and remove app
            </button>
          </div>
        </div>
      )}

      {/* ─── State: verifying ─── */}
      {wizardState === 'verifying' && (
        <div className="wizard-body" style={{ padding: '32px 16px', textAlign: 'center' }}>
          <Spinner size={32} />
          <p style={{ fontSize: 14, fontWeight: 500, marginTop: 12 }}>Testing connection...</p>
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            Verifying authentication and scanning for available capabilities.
          </p>
          {rec?.connectorAppId && (
            <div style={{ marginTop: 16, padding: 10, borderRadius: 8, background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', textAlign: 'left', fontSize: 12 }}>
              {rec.customerTenantName && <InfoRow label="Tenant" value={rec.customerTenantName} />}
              <InfoRow label="App ID" value={rec.connectorAppId} mono />
            </div>
          )}
        </div>
      )}

      {/* ─── State: success ─── */}
      {wizardState === 'success' && (
        <div className="wizard-body" style={{ padding: '24px 16px' }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--sev-success)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, margin: '0 auto 8px' }}>{'\u2713'}</div>
            <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Connected successfully</p>
          </div>

          <div style={{ padding: 12, borderRadius: 8, background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', marginBottom: 16 }}>
            {rec?.customerTenantName && <InfoRow label="Tenant" value={rec.customerTenantName} />}
            {rec?.connectorAppId && <InfoRow label="App ID" value={rec.connectorAppId} mono />}
            <InfoRow label="App name" value="Cade Security Connector" />
            <InfoRow label="Auth mode" value="Auto-provisioned" />
          </div>

          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 16, textAlign: 'center' }}>
            Credentials auto-managed by Cade. Rotation is automatic.
          </div>

          <button type="button" className="btn-primary" onClick={onComplete} style={{ width: '100%' }}>
            Go to Integrations
          </button>
        </div>
      )}

      {/* ─── State: failed ─── */}
      {wizardState === 'failed' && (
        <div className="wizard-body" style={{ padding: '24px 16px' }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--sev-high)', marginBottom: 8 }}>Setup failed</p>
          {error && <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 16, wordBreak: 'break-word', padding: 10, background: 'var(--color-bg-elevated)', borderRadius: 6, border: '1px solid var(--color-border)' }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-primary" onClick={() => { void handleRetry(); }} disabled={retryMut.isPending} style={{ flex: 1 }}>
              {retryMut.isPending ? (<><Spinner size={14} /> Retrying...</>) : 'Retry'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => { void handleCancel(); }}>Cancel</button>
            <button type="button" className="btn-secondary" onClick={onFallbackToManual}>Manual setup</button>
          </div>
        </div>
      )}

      {/* ─── State: cancelled ─── */}
      {wizardState === 'cancelled' && (
        <div className="wizard-body" style={{ textAlign: 'center', padding: '32px 16px' }}>
          <p style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>Setup cancelled. Any partial resources have been cleaned up.</p>
          <button type="button" className="btn-primary" onClick={onComplete} style={{ marginTop: 16 }}>Back to Integrations</button>
        </div>
      )}
    </div>
  );
}
